import { Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import { EmptyState } from "@/shared/components/EmptyState";
import { ISSUE_LABELS } from "@/features/patient/medications/components/medicationReviewBadges";
import type { MyReviewItem } from "@/portals/clinician/api/reviewQueueApi";
import { formatDate } from "@/features/admin/clinicians/components/formatDate";
import { useQuery } from "@tanstack/react-query";
import { myItemsQuery } from "@/portals/clinician/api/clinicianQueries";
import { AttachedViaTag, ReviewStatusBadge, MedicineNameCell } from "./reviewBadges";

export const MINE_EMPTY = "You have no items right now.";

/** The clinician's own items (claimed or assigned) — one cache, shared by the dashboard tab and the My Items page. */
export function useMyReviewItems() {
  return useQuery(myItemsQuery());
}

const columns: ColumnsType<MyReviewItem> = [
  { title: "Issue", key: "issue", render: (_, i) => <span className="font-semibold text-[#0A2947]">{ISSUE_LABELS[i.triggerType]}</span> },
  { title: "Medicine", key: "medicineName", render: (_, i) => <MedicineNameCell name={i.medicineName} triggerType={i.triggerType} /> },
  { title: "Patient age", key: "patientAge", render: (_, i) => i.patientAge ?? "—" },
  {
    title: "Status",
    key: "status",
    render: (_, i) => (
      <div className="flex flex-wrap items-center gap-1.5">
        <ReviewStatusBadge status={i.itemStatus} />
        {i.awaitingPatientReply && (
          <span className="rounded-full bg-[#FEF6DC] px-2 py-0.5 text-[11px] font-bold text-[#946200]">Waiting for patient</span>
        )}
      </div>
    ),
  },
  { title: "Via", key: "via", render: (_, i) => <AttachedViaTag via={i.attachedVia} /> },
  { title: "Attached", key: "attachedAt", render: (_, i) => formatDate(i.attachedAt) },
];

export function MyItemsTable({ items, loading, onOpenItem }: { items: MyReviewItem[]; loading: boolean; onOpenItem: (id: string) => void }) {
  return (
    <Table<MyReviewItem>
      rowKey="id"
      columns={columns}
      dataSource={items}
      loading={loading}
      pagination={{ pageSize: 10, hideOnSinglePage: true }}
      locale={{ emptyText: <EmptyState variant="compact" title={MINE_EMPTY} /> }}
      scroll={{ x: 660 }}
      rowClassName="cursor-pointer"
      onRow={(item) => ({
        onClick: () => onOpenItem(item.id),
        onKeyDown: (e) => { if (e.key === "Enter") onOpenItem(item.id); },
        tabIndex: 0,
        "aria-label": `Open ${ISSUE_LABELS[item.triggerType]}`,
      })}
    />
  );
}
